<template>
    <div class="datetime">
        <div class="date">
            <v-menu
                v-model="menu_date"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
            >
                <template #activator="{ on }">
                    <v-text-field
                        v-model="date"
                        readonly
                        v-on="on"
                    >
                        <div class="v-input__icon v-input__icon--append" 
                            slot="append"
                        >
                            <v-icon color="black">
                                icon-time-setting
                            </v-icon>
                        </div>
                    </v-text-field>
                </template>

                <v-date-picker 
                    v-model="model_date"
                    :locale="language !== 'en'? 'zh-tw' : 'en' "
                    :day-format="date => date.split('-')[2]"
                    :min="min_case_date"
                    :max="max_date"
                >
                    <v-spacer />
                    <v-btn
                        text
                        color="primary"
                        @click="cancel_date_pick"
                    >
                        {{$t('__time_choose.cancel')}}
                    </v-btn>
                    <v-btn
                        text
                        color="primary"
                        @click="update_date_by_user"
                    >
                        {{$t('__time_choose.decide')}}
                    </v-btn>
                </v-date-picker>
            </v-menu>
        </div>   
        <div class="time">
            <v-menu
                v-if="is_show_time_choose_field"
                v-model="menu_time"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
            >
                <template #activator="{ on }">
                    <v-text-field
                        v-model="time"
                        readonly
                        v-on="on"
                    >
                        <div class="v-input__icon v-input__icon--append" 
                            slot="append"
                        >
                            <v-icon color="black">
                                icon-clock
                            </v-icon>
                        </div>
                    </v-text-field>
                </template>
                <v-time-picker 
                    v-if="menu_time"
                    v-model="model_time"
                    :locale="language !== 'en'? 'zh-tw' : 'en' "
                    format="24hr"
                    :allowed-hours="allow_hours"
                    :allowed-minutes="allow_minutes"
                    :min="min_case_time"
                    :max="max_time"
                >
                    <v-btn
                        text
                        color="primary"
                        @click="back_to_date_picker"
                    >
                        <v-icon size="16">
                            icon-arrow-back
                        </v-icon>
                        {{ $t('__time_choose.back_to_date_select') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        text
                        color="primary"
                        @click="cancel_time_pick"
                    >
                        {{ $t('__time_choose.cancel') }}
                    </v-btn>
                    <v-btn
                        text
                        color="primary"
                        @click="update_time_by_user"
                    >
                        {{ $t('__time_choose.decide') }}
                    </v-btn>
                </v-time-picker>
            </v-menu>
        </div>  
        <div class="btn-ctrl no-shot">
            <v-btn
                text
                color="black"
                @click="update_time_by_interval(-1)"
            >
                <v-icon>
                    mdi-chevron-left
                </v-icon>
                <template v-if="time_interval==='day'">前一天</template>
                <template v-else>前{{interval_minutes}}分鐘</template>
            </v-btn>
            <v-btn
                text
                color="black"
                @click="update_time_by_interval(1)"
            >
                <template v-if="time_interval==='day'">後一天</template>
                <template v-else>後{{interval_minutes}}分鐘</template>
                <v-icon>
                    mdi-chevron-right
                </v-icon>
            </v-btn>
        </div>
    </div>
</template>

<script>
import * as dayjs from 'dayjs'
import { timestamp_to_date, timestamp_to_time, compute_case_limit_time } from '@/utils/case/case.js'
import { mapGetters, mapState } from 'vuex'
import { STATIC_SVG_ROOT } from '@/config/config.js'
export default {
    name: 'QpeDatetime',
    props: {
        props_date: {
            type: String,
            default() {
                return new Date(+new Date() + 8 * 3600000).toISOString().substr(0, 10)
            },
        },

        props_time: {
            type: String,
            default() {
                return `${new Date().getHours()}:${('0' + new Date().getMinutes()).slice(-2, -1)}0`
            },
        },

        allow_hour_list: {
            type: Array,
            default() {
                return Array.from({ length: 24 }).map((o, i) => i)
            },
        },
    },
    data() {
        return {
            date: new Date(+new Date() + 8 * 3600000).toISOString().substr(0, 10),
            model_date: new Date(+new Date() + 8 * 3600000).toISOString().substr(0, 10),
            time: `${new Date().getHours()}:${('0' + new Date().getMinutes()).slice(-2, -1)}0`,
            model_time: `${new Date().getHours()}:${('0' + new Date().getMinutes()).slice(-2, -1)}0`,
            menu_date: false,
            menu_time: false,

            STATIC_SVG_ROOT: STATIC_SVG_ROOT,
        }
    },
    computed: {
        ...mapState(['language']),

        ...mapState('monitor', ['time_interval']),

        ...mapState('case', ['case_selected']),

        ...mapGetters('monitor', ['is_show_time_choose_field']),

        ...mapGetters('case', ['is_case_mode']),

        min_case_date() {
            return this.is_case_mode ? timestamp_to_date(this.case_selected.start) : ''
        },

        max_date() {
            if (this.is_case_mode) {
                return timestamp_to_date(this.case_selected.end)
            }

            const today_date = timestamp_to_date()
            return today_date
        },

        min_case_time() {
            return !this.is_case_mode ? '' : compute_case_limit_time(this.case_selected.start, this.date)
        },

        max_time() {
            if (this.is_case_mode) {
                return compute_case_limit_time(this.case_selected.end, this.date)
            }

            const today_date = timestamp_to_date()
            return this.date === today_date ? timestamp_to_time() : ''
        },

        interval_minutes() {
            if (this.time_interval === 'day') return 60 * 24
            if (this.time_interval === 'hour') return 60
            const match_min = this.time_interval.match(/(\d+)\s*min/i)
            if (match_min && isInt(Number(match_min[1]))) {
                return Number(match_min[1])
            }

            return 10
        },
    },
    watch: {
        props_date: {
            handler(date_value) {
                this.date = date_value || this.date
                this.model_date = date_value || this.model_date
            },
            immediate: true,
        },
        props_time: {
            handler(time_value) {
                this.time = time_value || this.time
                this.model_time = time_value || this.model_time
            },
            immediate: true,
        },
    },
    methods: {
        cancel_date_pick() {
            this.menu_date = false
            this.model_date = this.date
        },

        cancel_time_pick() {
            this.menu_time = false
            this.model_time = this.time
        },

        back_to_date_picker() {
            this.menu_time = false
            this.menu_date = true
        },

        update_date_by_user() {
            this.menu_date = false
            this.date = this.model_date
            this.time = this.is_show_time_choose_field ? this.time : '08:00'
            this.update_time()
        },

        update_time_by_user() {
            this.menu_time = false
            this.time = this.model_time
            this.update_time()
        },

        update_time_by_interval(sign) {
            const dt = dayjs(`${this.date} ${this.time}:00`).add(sign * this.interval_minutes, 'minute')
            this.date = dt.format('YYYY-MM-DD')
            this.model_date = dt.format('YYYY-MM-DD')
            this.time = dt.format('HH:mm')
            this.model_time = dt.format('HH:mm')
            this.update_time()
        },

        update_time() {
            const new_datetime = `${this.date} ${this.time}:00`
            this.$emit('update-time', new_datetime)

            // 使用者更改時間後關閉自動更新功能
            this.$emit('change-parent-date-time', this.date, this.time)
        },

        allow_hours(hour) {
            return this.allow_hour_list.includes(hour)
        },

        allow_minutes(minute) {
            if (this.time_interval === 'hour') {
                return minute % 60 === 0
            }

            return minute % this.interval_minutes === 0
        },
    },
}
</script>

<style lang="scss" scoped>
.datetime {
    display: flex;
    align-items: center;
}

.date {
    width: 124px;
}

.time {
    margin: 0 16px;
    width: 76px;
}
</style>

<style lang="scss">
.date,
.time {
    input {
        cursor: pointer;
    }
}
</style>
